import { useEffect, useMemo, useState } from 'react';
import { hasDashboardFullAccess } from '../../utils/workspaces';
import Select from 'react-select';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';

const STATUS_COLORS = {
  pending: { bg: '#fee2e2', color: '#991b1b' },
  paid: { bg: '#dcfce7', color: '#166534' },
  change: { bg: '#fef3c7', color: '#92400e' },
  informed: { bg: '#e0f2fe', color: '#0c4a6e' },
  confirmed: { bg: '#ede9fe', color: '#5b21b6' },
  'No Answer': { bg: '#e5e7eb', color: '#374151' },
};

const STATUS_OPTIONS = ['all', 'pending', 'paid', 'change', 'informed', 'confirmed', 'No Answer'];

const REG_STATUS_FILTER_LABELS = {
  active: 'Active',
  pending: 'Pending',
  postponed: 'Postponed',
  suspending: 'Suspending',
  inactivating: 'Inactivating',
  inactivated: 'Inactivated',
  suspended: 'Suspended',
};

const REG_STATUS_COLORS = {
  active: { bg: '#16a34a', color: '#ffffff' },
  pending: { bg: '#d97706', color: '#ffffff' },
  postponed: { bg: '#0284c7', color: '#ffffff' },
  suspending: { bg: '#ea580c', color: '#ffffff' },
  inactivating: { bg: '#dc2626', color: '#ffffff' },
  inactivated: { bg: '#7f1d1d', color: '#fecaca' },
  suspended: { bg: '#475569', color: '#f1f5f9' },
};

const fmt = (value) => {
  const parsed = typeof value === 'number' ? value : Number(String(value ?? '').replace(/,/g, '').trim());
  if (Number.isNaN(parsed)) return '—';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(parsed);
};

const fmtShort = (value) => {
  const amount = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(amount)) return '0';
  if (Math.abs(amount) >= 1_000_000) return `${(amount / 1_000_000).toFixed(2)}M`;
  if (Math.abs(amount) >= 1_000) return `${(amount / 1_000).toFixed(1)}K`;
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(amount);
};

const formatDueDate = (value) => {
  if (!value) return '—';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTime = (value) => {
  if (!value) return '—';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMonthTitle = (value) => {
  if (!value) return 'All Months';

  const parsed = new Date(`${value}-01`);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleString('default', {
    month: 'long',
    year: 'numeric',
  });
};

const toInputDateValue = (value) => {
  if (!value) return '';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getCopyableContactNo = (value) => {
  if (!value) return '';
  const contact = String(value).trim();
  if (!contact) return '';
  return contact.startsWith('0') && contact.length > 1 ? contact.slice(1) : contact;
};

export default function MyCourseDue() {
  const [records, setRecords] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [regStatusFilter, setRegStatusFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState(null);
  const [batchFilter, setBatchFilter] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);
  const [inlineError, setInlineError] = useState('');
  const [monthFilter, setMonthFilter] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [courseSummaryModalOpen, setCourseSummaryModalOpen] = useState(false);
  const [staffContributionModalOpen, setStaffContributionModalOpen] = useState(false);
  const [staffContribution, setStaffContribution] = useState([]);
  const [staffContributionLoading, setStaffContributionLoading] = useState(false);
  const [followUpModal, setFollowUpModal] = useState({ isOpen: false, row: null });
  const [copiedContact, setCopiedContact] = useState('');
  const [followUpHistory, setFollowUpHistory] = useState([]);
  const [followUpForm, setFollowUpForm] = useState({
    comment: '',
    nextReminderDate: '',
    nextReminderComment: '',
    submitting: false,
    loadingHistory: false,
    error: '',
  });

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        const searchParams = new URLSearchParams();
        if (monthFilter) searchParams.set('month', monthFilter);
        searchParams.set('scope', 'my-courses');

        const response = await axiosInstance.get(`/admin/recovery-dues?${searchParams.toString()}`);
        if (!cancelled) {
          setRecords(response?.data?.dues ?? []);
          setAvailableMonths(response?.data?.availableMonths ?? []);
        }
      } catch {
        if (!cancelled) {
          setRecords([]);
          setAvailableMonths([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [monthFilter]);

  const isSuperAdmin = hasDashboardFullAccess(AuthService.getUser());

  const openStaffContributionModal = async () => {
    setStaffContributionModalOpen(true);
    setStaffContribution([]);
    setStaffContributionLoading(true);
    try {
      const params = new URLSearchParams();
      if (monthFilter) params.set('month', monthFilter);
      const response = await axiosInstance.get(`/admin/recovery-dues/staff-contribution?${params.toString()}`);
      setStaffContribution(response?.data?.data ?? []);
    } catch {
      setStaffContribution([]);
    } finally {
      setStaffContributionLoading(false);
    }
  };

  const courseOptions = useMemo(() => {
    const unique = [...new Set(records.map((record) => record.course).filter(Boolean))].sort();
    return unique.map((course) => ({ value: course, label: course }));
  }, [records]);

  const batchOptions = useMemo(() => {
    const unique = [...new Set(records.map((record) => record.batchName).filter(Boolean))].sort();
    return unique.map((batch) => ({ value: batch, label: batch }));
  }, [records]);

  const currentMonth = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  // Always include the current month in the picker so the default selection is
  // always visible even when no records exist for the current month yet.
  const monthOptions = useMemo(() => {
    if (availableMonths.includes(currentMonth)) return availableMonths;
    return [currentMonth, ...availableMonths];
  }, [availableMonths, currentMonth]);

  const regStatusOptions = useMemo(() => {
    const unique = [...new Set(records
      .map((record) => String(record.regStatus || '').trim().toLowerCase())
      .filter(Boolean))].sort();

    return ['all', ...unique];
  }, [records]);

  const summaryScope = useMemo(() => records.filter((record) => {
    const matchCourse = !courseFilter || record.course === courseFilter.value;
    const matchBatch = !batchFilter || record.batchName === batchFilter.value;
    const matchStatus = statusFilter === 'all' || record.dueStatus === statusFilter;
    const regStatusKey = String(record.regStatus || '').trim().toLowerCase();
    const matchRegStatus = regStatusFilter === 'all' || regStatusKey === regStatusFilter;
    return matchCourse && matchBatch && matchStatus && matchRegStatus;
  }), [records, courseFilter, batchFilter, statusFilter, regStatusFilter]);

  const filtered = useMemo(() => summaryScope.filter((record) => {
    const searchValue = search.trim().toLowerCase();
    const matchSearch = !searchValue
      || String(record.studentName || '').toLowerCase().includes(searchValue)
      || String(record.studentId || '').toLowerCase().includes(searchValue)
      || String(record.batchName || '').toLowerCase().includes(searchValue)
      || String(record.course || '').toLowerCase().includes(searchValue);
    return matchSearch;
  }), [summaryScope, search]);

  const summary = useMemo(() => {
    let totalDueAmount = 0;
    let totalPaidAmount = 0;
    let totalConfirmedAmount = 0;
    let followupCount = 0;
    const statusCounts = {};

    summaryScope.forEach((record) => {
      totalDueAmount += parseFloat(record.dueAmount) || 0;
      totalConfirmedAmount += parseFloat(record.confirmedAmount) || 0;
      if (record.dueStatus === 'paid') {
        totalPaidAmount += parseFloat(record.confirmedAmount) || 0;
      }
      followupCount += Number(record.followupCount) || 0;

      const statusKey = record.dueStatus || 'pending';
      statusCounts[statusKey] = (statusCounts[statusKey] || 0) + 1;
    });

    return {
      totalStudents: summaryScope.length,
      totalDueAmount,
      totalPaidAmount,
      totalConfirmedAmount,
      followupCount,
      statusCounts,
      remainingAmount: Math.max(0, totalDueAmount - totalPaidAmount),
      collectionPct: totalDueAmount > 0 ? Math.min(100, (totalPaidAmount / totalDueAmount) * 100) : 0,
    };
  }, [summaryScope]);

  const departmentSummary = useMemo(() => {
    const grouped = new Map();

    summaryScope.forEach((record) => {
      const key = record.course || 'Unassigned Course';
      const current = grouped.get(key) || {
        course: key,
        totalStudents: 0,
        totalDueAmount: 0,
        totalPaidAmount: 0,
        totalConfirmedAmount: 0,
        followupCount: 0,
      };

      current.totalStudents += 1;
      current.totalDueAmount += parseFloat(record.dueAmount) || 0;
      current.totalConfirmedAmount += parseFloat(record.confirmedAmount) || 0;
      current.followupCount += Number(record.followupCount) || 0;
      if (record.dueStatus === 'paid') {
        current.totalPaidAmount += parseFloat(record.confirmedAmount) || 0;
      }

      grouped.set(key, current);
    });

    return Array.from(grouped.values())
      .map((item) => ({
        ...item,
        remainingAmount: Math.max(0, item.totalDueAmount - item.totalPaidAmount),
        collectionPct: item.totalDueAmount > 0 ? Math.min(100, (item.totalPaidAmount / item.totalDueAmount) * 100) : 0,
      }))
      .sort((left, right) => right.totalPaidAmount - left.totalPaidAmount || right.totalDueAmount - left.totalDueAmount);
  }, [summaryScope]);

  const startEditingCell = (row, field) => {
    let nextValue = '';

    if (field === 'dueStatus') nextValue = row.dueStatus || 'pending';
    if (field === 'confirmedAmount') nextValue = row.confirmedAmount ?? '';
    if (field === 'confirmDateToPay') nextValue = toInputDateValue(row.confirmDateToPay);

    setInlineError('');
    setEditingCell({ dueId: row.dueId, field });
    setEditingValue(String(nextValue));
  };

  const stopEditingCell = () => {
    setEditingCell(null);
    setEditingValue('');
    setSavingCell(null);
    setInlineError('');
  };

  const updateRecordFields = (dueId, patch) => {
    setRecords((previous) => previous.map((record) => (
      record.dueId === dueId ? { ...record, ...patch } : record
    )));

    setFollowUpModal((previous) => {
      if (!previous.row || previous.row.dueId !== dueId) return previous;
      return { ...previous, row: { ...previous.row, ...patch } };
    });
  };

  const saveInlineEdit = async (row, field, rawValue) => {
    if (!row?.dueId) return;

    let payload = {};
    let nextPatch = {};

    if (field === 'dueStatus') {
      payload = { dueStatus: rawValue };
      nextPatch = { dueStatus: rawValue };
      if ((row.dueStatus || '') === rawValue) {
        stopEditingCell();
        return;
      }
    }

    if (field === 'confirmedAmount') {
      const normalized = String(rawValue).trim();
      const currentValue = row.confirmedAmount === null || row.confirmedAmount === undefined ? '' : String(row.confirmedAmount);
      if (normalized === currentValue) {
        stopEditingCell();
        return;
      }
      payload = { confirmedAmount: normalized || null };
      nextPatch = { confirmedAmount: normalized || null };
    }

    if (field === 'confirmDateToPay') {
      const normalized = String(rawValue).trim();
      const currentValue = toInputDateValue(row.confirmDateToPay);
      if (normalized === currentValue) {
        stopEditingCell();
        return;
      }
      payload = { confirmDateToPay: normalized || null };
      nextPatch = { confirmDateToPay: normalized || null };
    }

    try {
      setInlineError('');
      setSavingCell({ dueId: row.dueId, field });
      const response = await axiosInstance.patch(`/admin/recovery-dues/${row.dueId}`, payload);
      updateRecordFields(row.dueId, response?.data?.data || nextPatch);
      stopEditingCell();
    } catch (error) {
      const message = error?.response?.data?.errors?.[field]?.[0]
        || error?.response?.data?.message
        || 'Failed to update this field.';
      setSavingCell(null);
      setInlineError(message);
    }
  };

  const isEditingCell = (dueId, field) => editingCell?.dueId === dueId && editingCell?.field === field;
  const isSavingCell = (dueId, field) => savingCell?.dueId === dueId && savingCell?.field === field;

  const syncFollowUpCount = (dueId, count) => {
    setRecords((previous) => previous.map((record) => (
      record.dueId === dueId ? { ...record, followupCount: count } : record
    )));

    setFollowUpModal((previous) => {
      if (!previous.row || previous.row.dueId !== dueId) return previous;
      return { ...previous, row: { ...previous.row, followupCount: count } };
    });
  };

  const loadFollowUpHistory = async (dueId) => {
    if (!dueId) {
      setFollowUpHistory([]);
      return;
    }

    try {
      setFollowUpForm((previous) => ({ ...previous, loadingHistory: true, error: '' }));
      const response = await axiosInstance.get(`/admin/recovery-dues/${dueId}/followups`);
      const items = response?.data?.data ?? [];
      setFollowUpHistory(items);
      syncFollowUpCount(dueId, items.length);
    } catch {
      setFollowUpHistory([]);
      setFollowUpForm((previous) => ({ ...previous, error: 'Failed to load follow-up history.' }));
    } finally {
      setFollowUpForm((previous) => ({ ...previous, loadingHistory: false }));
    }
  };

  const openFollowUpModal = async (row) => {
    setFollowUpModal({ isOpen: true, row });
    setFollowUpHistory([]);
    setFollowUpForm({
      comment: '',
      nextReminderDate: '',
      nextReminderComment: '',
      submitting: false,
      loadingHistory: false,
      error: '',
    });
    await loadFollowUpHistory(row?.dueId);
  };

  const closeFollowUpModal = () => {
    setFollowUpModal({ isOpen: false, row: null });
    setFollowUpHistory([]);
    setFollowUpForm({
      comment: '',
      nextReminderDate: '',
      nextReminderComment: '',
      submitting: false,
      loadingHistory: false,
      error: '',
    });
  };

  const submitFollowUp = async () => {
    if (!followUpModal.row?.dueId) return;

    const payload = {
      comment: followUpForm.comment.trim(),
      next_reminder_date: followUpForm.nextReminderDate.trim() || null,
      next_reminder_comment: followUpForm.nextReminderComment.trim() || null,
    };

    if (!payload.comment) {
      setFollowUpForm((previous) => ({ ...previous, error: 'Comment is required.' }));
      return;
    }

    try {
      setFollowUpForm((previous) => ({ ...previous, submitting: true, error: '' }));
      await axiosInstance.post(`/admin/recovery-dues/${followUpModal.row.dueId}/followups`, payload);
      await loadFollowUpHistory(followUpModal.row.dueId);
      setFollowUpForm({
        comment: '',
        nextReminderDate: '',
        nextReminderComment: '',
        submitting: false,
        loadingHistory: false,
        error: '',
      });
    } catch (error) {
      const message = error?.response?.data?.errors?.comment?.[0]
        || error?.response?.data?.message
        || 'Failed to save follow-up.';
      setFollowUpForm((previous) => ({ ...previous, submitting: false, error: message }));
    }
  };

  const handleCopyContact = async (contactNo) => {
    const copyValue = getCopyableContactNo(contactNo);
    if (!copyValue || !navigator?.clipboard?.writeText) return;

    try {
      await navigator.clipboard.writeText(copyValue);
      setCopiedContact(contactNo);
      window.setTimeout(() => {
        setCopiedContact((current) => (current === contactNo ? '' : current));
      }, 1500);
    } catch {
      setCopiedContact('');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="My Course Due">
        <div style={{ display: 'grid', gap: 20 }}>
          <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: 18, padding: '22px 24px', color: '#e2e8f0', boxShadow: '0 20px 40px rgba(15, 23, 42, 0.12)' }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: '#93c5fd' }}>Coordinator Recovery View</div>
            <div style={{ marginTop: 12, height: 34, width: 220, borderRadius: 10, background: 'rgba(255,255,255,0.18)' }} className="animate-pulse" />
            <div style={{ marginTop: 12, height: 16, width: '72%', maxWidth: 760, borderRadius: 999, background: 'rgba(255,255,255,0.14)' }} className="animate-pulse" />
            <div style={{ marginTop: 8, height: 16, width: '58%', maxWidth: 620, borderRadius: 999, background: 'rgba(255,255,255,0.12)' }} className="animate-pulse" />
            <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14 }}>
              {[1, 2, 3, 4].map((item) => (
                <div key={item} style={{ borderRadius: 14, padding: '14px 16px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(148, 163, 184, 0.16)' }}>
                  <div style={{ height: 12, width: 72, borderRadius: 999, background: 'rgba(255,255,255,0.18)' }} className="animate-pulse" />
                  <div style={{ marginTop: 10, height: 24, width: 90, borderRadius: 10, background: 'rgba(255,255,255,0.16)' }} className="animate-pulse" />
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex-1 space-y-3">
                    <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                    <div className="h-10 w-28 animate-pulse rounded bg-gray-300"></div>
                    <div className="h-3 w-32 animate-pulse rounded bg-gray-100"></div>
                  </div>
                  <div className="h-16 w-16 animate-pulse rounded-2xl bg-gray-200"></div>
                </div>
                <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent"></div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {[1, 2].map((item) => (
              <div key={item} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-3">
                    <div className="h-5 w-40 animate-pulse rounded bg-gray-200" />
                    <div className="h-4 w-56 animate-pulse rounded bg-gray-100" />
                  </div>
                  <div className="h-10 w-24 animate-pulse rounded-full bg-gray-100" />
                </div>
                <div className="mt-6 grid grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map((metric) => (
                    <div key={metric} className="rounded-xl bg-slate-50 p-4">
                      <div className="h-3 w-20 animate-pulse rounded bg-gray-200" />
                      <div className="mt-3 h-7 w-24 animate-pulse rounded bg-gray-300" />
                    </div>
                  ))}
                </div>
                <div className="mt-5 h-2 w-full animate-pulse rounded-full bg-gray-100" />
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-3">
                <div className="h-6 w-48 animate-pulse rounded bg-gray-200" />
                <div className="h-4 w-72 animate-pulse rounded bg-gray-100" />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:w-[540px]">
                {[1, 2, 3].map((filter) => (
                  <div key={filter} className="h-11 animate-pulse rounded-xl bg-slate-100" />
                ))}
              </div>
            </div>
            <div className="mt-6 overflow-hidden rounded-2xl border border-gray-100">
              {[1, 2, 3, 4, 5].map((row) => (
                <div key={row} className="grid grid-cols-12 gap-4 border-b border-gray-100 px-5 py-4 last:border-b-0">
                  <div className="col-span-3 h-4 animate-pulse rounded bg-gray-200" />
                  <div className="col-span-2 h-4 animate-pulse rounded bg-gray-100" />
                  <div className="col-span-2 h-4 animate-pulse rounded bg-gray-100" />
                  <div className="col-span-2 h-4 animate-pulse rounded bg-gray-100" />
                  <div className="col-span-1 h-8 animate-pulse rounded-full bg-gray-100" />
                  <div className="col-span-2 h-8 animate-pulse rounded-xl bg-gray-100" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Course Due">
      <style>{`
        @keyframes coordinatorDueModalFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes coordinatorDueFollowupSlideIn {
          from {
            transform: translateX(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>

      <div style={{ display: 'grid', gap: 20 }}>
        <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: 18, padding: '22px 24px', color: '#e2e8f0', boxShadow: '0 20px 40px rgba(15, 23, 42, 0.12)' }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: '#93c5fd' }}>
            {monthFilter ? 'Monthly Due Overview' : 'All-Time Due Overview'}
          </div>
          <h2 style={{ margin: '8px 0 0', fontSize: 26, fontWeight: 700, color: '#fff' }}>
            {monthFilter ? `${formatMonthTitle(monthFilter)} — Course Due` : 'All Months — Course Due'}
          </h2>
          <p style={{ margin: '10px 0 0', fontSize: 14, color: '#cbd5e1', maxWidth: 920, lineHeight: 1.6 }}>
            {monthFilter
              ? 'Review the selected month\'s due balance across all your courses, track what has been collected, and open the course-wise summary for a complete breakdown.'
              : 'Showing dues across all months for your courses. Use the month picker or click "This Month" to narrow down to a specific period.'}
          </p>
          <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 18 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#93c5fd' }}>
                {monthFilter ? 'Selected Month' : 'Period'}
              </div>
              <div style={{ marginTop: 4, fontSize: 18, fontWeight: 700, color: '#fff' }}>
                {monthFilter ? formatMonthTitle(monthFilter) : 'All Months'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#93c5fd' }}>Total Courses</div>
              <div style={{ marginTop: 4, fontSize: 18, fontWeight: 700, color: '#fff' }}>{departmentSummary.length}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#93c5fd' }}>
                {monthFilter ? 'Month Remaining Due' : 'Total Remaining Due'}
              </div>
              <div style={{ marginTop: 4, fontSize: 18, fontWeight: 700, color: '#fca5a5' }}>Rs {fmt(summary.remainingAmount)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#93c5fd' }}>Total Follow-ups</div>
              <div style={{ marginTop: 4, fontSize: 18, fontWeight: 700, color: '#fff' }}>{summary.followupCount}</div>
            </div>
          </div>
          <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            {isSuperAdmin && (
              <button
                type="button"
                onClick={openStaffContributionModal}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  borderRadius: 12,
                  border: '1px solid rgba(167, 243, 208, 0.35)',
                  background: 'rgba(209, 250, 229, 0.12)',
                  color: '#d1fae5',
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  backdropFilter: 'blur(8px)',
                }}
              >
                Staff Contribution
              </button>
            )}
            <button
              type="button"
              onClick={() => setCourseSummaryModalOpen(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                borderRadius: 12,
                border: '1px solid rgba(147, 197, 253, 0.35)',
                background: 'rgba(219, 234, 254, 0.12)',
                color: '#eff6ff',
                padding: '10px 16px',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                backdropFilter: 'blur(8px)',
              }}
            >
              Course Wise Summary
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '16px 18px', borderLeft: '4px solid #dc2626', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>Total Due</div>
            <div style={{ marginTop: 6, fontSize: 22, fontWeight: 700, color: '#dc2626' }}>Rs {fmt(summary.totalDueAmount)}</div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#94a3b8' }}>{fmtShort(summary.totalDueAmount)} across your departments</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 14, padding: '16px 18px', borderLeft: '4px solid #16a34a', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>Collected</div>
            <div style={{ marginTop: 6, fontSize: 22, fontWeight: 700, color: '#16a34a' }}>Rs {fmt(summary.totalPaidAmount)}</div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#94a3b8' }}>{summary.collectionPct.toFixed(1)}% of total due recovered</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 14, padding: '16px 18px', borderLeft: '4px solid #0369a1', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>Committed</div>
            <div style={{ marginTop: 6, fontSize: 22, fontWeight: 700, color: '#0369a1' }}>Rs {fmt(summary.totalConfirmedAmount)}</div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#94a3b8' }}>Amounts students promised to settle</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 14, padding: '16px 18px', borderLeft: '4px solid #7c3aed', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>Students</div>
            <div style={{ marginTop: 6, fontSize: 22, fontWeight: 700, color: '#7c3aed' }}>{summary.totalStudents}</div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#94a3b8' }}>Current due records in your scope</div>
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 16, padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>Collected Progress</div>
              <div style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                Rs {fmt(summary.totalPaidAmount)} collected out of Rs {fmt(summary.totalDueAmount)}
              </div>
              <div style={{ marginTop: 6, fontSize: 13, color: '#64748b' }}>
                This percentage updates automatically based on the selected month, batch, and course filters.
              </div>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: summary.collectionPct >= 70 ? '#16a34a' : summary.collectionPct >= 35 ? '#d97706' : '#dc2626' }}>
              {summary.collectionPct.toFixed(1)}%
            </div>
          </div>

          <div style={{ marginTop: 16, height: 14, borderRadius: 999, background: '#e2e8f0', overflow: 'hidden' }}>
            <div
              style={{
                width: `${summary.collectionPct}%`,
                height: '100%',
                borderRadius: 999,
                background: summary.collectionPct >= 70
                  ? 'linear-gradient(90deg, #16a34a 0%, #22c55e 100%)'
                  : summary.collectionPct >= 35
                    ? 'linear-gradient(90deg, #d97706 0%, #f59e0b 100%)'
                    : 'linear-gradient(90deg, #dc2626 0%, #f87171 100%)',
                transition: 'width 220ms ease',
              }}
            />
          </div>

          <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', fontSize: 12, color: '#64748b' }}>
            <span>0%</span>
            <span style={{ fontWeight: 700, color: '#0f172a' }}>{summary.totalStudents} student records</span>
            <span>100%</span>
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 14, padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <input
              type="text"
              placeholder="Search by student, registration, course, or batch"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={{ flex: '1 1 260px', height: 40, borderRadius: 10, border: '1px solid #e2e8f0', padding: '0 12px', fontSize: 14, outline: 'none' }}
            />
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: '0 0 auto' }}>
              <select
                value={monthFilter}
                onChange={(event) => setMonthFilter(event.target.value)}
                style={{ height: 40, borderRadius: 10, border: '1px solid #e2e8f0', padding: '0 12px', fontSize: 14, background: '#fff', outline: 'none', minWidth: 180 }}
              >
                <option value="">All Months</option>
                {monthOptions.map((month) => (
                  <option key={month} value={month}>{formatMonthTitle(month)}</option>
                ))}
              </select>
              {monthFilter !== '' ? (
                <button
                  type="button"
                  onClick={() => setMonthFilter('')}
                  title="Show dues from all months"
                  style={{
                    height: 40,
                    borderRadius: 10,
                    border: '1px solid #e2e8f0',
                    background: '#f8fafc',
                    color: '#334155',
                    padding: '0 14px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Load All
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setMonthFilter(currentMonth)}
                  title="Show only this month's dues"
                  style={{
                    height: 40,
                    borderRadius: 10,
                    border: 'none',
                    background: '#0f172a',
                    color: '#fff',
                    padding: '0 14px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  This Month
                </button>
              )}
            </div>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              style={{ height: 40, borderRadius: 10, border: '1px solid #e2e8f0', padding: '0 12px', fontSize: 14, background: '#fff', outline: 'none', minWidth: 160 }}
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status === 'all' ? 'All Statuses' : status}
                </option>
              ))}
            </select>
            <select
              value={regStatusFilter}
              onChange={(event) => setRegStatusFilter(event.target.value)}
              style={{ height: 40, borderRadius: 10, border: '1px solid #e2e8f0', padding: '0 12px', fontSize: 14, background: '#fff', outline: 'none', minWidth: 170 }}
            >
              {regStatusOptions.map((status) => (
                <option key={status} value={status}>
                  {status === 'all' ? 'All Reg. Statuses' : (REG_STATUS_FILTER_LABELS[status] || status)}
                </option>
              ))}
            </select>
            <div style={{ minWidth: 220, flex: '1 1 220px' }}>
              <Select
                isClearable
                placeholder="All Courses"
                options={courseOptions}
                value={courseFilter}
                onChange={setCourseFilter}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: 40,
                    borderRadius: 10,
                    border: state.isFocused ? '1px solid #94a3b8' : '1px solid #e2e8f0',
                    boxShadow: 'none',
                  }),
                  valueContainer: (base) => ({ ...base, padding: '0 10px' }),
                  indicatorSeparator: () => ({ display: 'none' }),
                }}
              />
            </div>
            <div style={{ minWidth: 220, flex: '1 1 220px' }}>
              <Select
                isClearable
                placeholder="All Batches"
                options={batchOptions}
                value={batchFilter}
                onChange={setBatchFilter}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: 40,
                    borderRadius: 10,
                    border: state.isFocused ? '1px solid #94a3b8' : '1px solid #e2e8f0',
                    boxShadow: 'none',
                  }),
                  valueContainer: (base) => ({ ...base, padding: '0 10px' }),
                  indicatorSeparator: () => ({ display: 'none' }),
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              Showing <span style={{ fontWeight: 700, color: '#0f172a' }}>{filtered.length}</span> of <span style={{ fontWeight: 700, color: '#0f172a' }}>{summary.totalStudents}</span> records
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setStatusFilter('all')}
                style={{
                  border: 'none',
                  borderRadius: 999,
                  background: statusFilter === 'all' ? '#0f172a' : '#e2e8f0',
                  color: statusFilter === 'all' ? '#fff' : '#334155',
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                All {summary.totalStudents}
              </button>
              {STATUS_OPTIONS.filter((status) => status !== 'all').map((status) => {
                const palette = STATUS_COLORS[status] || { bg: '#f1f5f9', color: '#475569' };
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setStatusFilter((current) => (current === status ? 'all' : status))}
                    style={{
                      border: 'none',
                      borderRadius: 999,
                      background: statusFilter === status ? palette.color : palette.bg,
                      color: statusFilter === status ? '#fff' : palette.color,
                      padding: '6px 12px',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {status} {summary.statusCounts[status] || 0}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {['#', 'Student', 'Total Due', 'Due Date', 'Status', 'Confirmed', 'Pay Date', 'Follow-up'].map((header) => (
                    <th key={header} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ padding: '40px 14px', textAlign: 'center' }}>
                      {records.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                          <div style={{ fontSize: 32 }}>📭</div>
                          <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>
                            No due records found for your mapped courses
                          </div>
                          <div style={{ color: '#64748b', fontSize: 13, maxWidth: 440, lineHeight: 1.6 }}>
                            {monthFilter
                              ? `There are no dues for ${formatMonthTitle(monthFilter)}. Try clicking "Load All" to view all months, or check that the correct courses are mapped to you.`
                              : 'No dues are linked to your courses yet. Make sure courses are mapped to your account in Due Course Mapping.'}
                          </div>
                          <a
                            href="/admin/due-course-mapping"
                            style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 10, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#0f172a', padding: '8px 16px', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}
                          >
                            Go to Due Course Mapping →
                          </a>
                        </div>
                      ) : (
                        <div style={{ color: '#94a3b8', fontSize: 14 }}>
                          No records match your current filters.
                        </div>
                      )}
                    </td>
                  </tr>
                ) : filtered.map((record, index) => {
                  const statusStyle = STATUS_COLORS[record.dueStatus] || { bg: '#f1f5f9', color: '#475569' };
                  const regStatusKey = String(record.regStatus || '').toLowerCase();
                  const regStatusStyle = REG_STATUS_COLORS[regStatusKey] || { bg: '#475569', color: '#f8fafc' };
                  const isPaid = record.dueStatus === 'paid';
                  return (
                    <tr key={record.dueId ?? index} style={{ borderBottom: '1px solid #e2e8f0', background: isPaid ? '#dcfce7' : 'transparent' }}>
                      <td style={{ padding: '12px 14px', color: '#94a3b8' }}>{index + 1}</td>
                      <td style={{ padding: '16px 14px', minWidth: 310 }}>
                        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>{record.studentName || '—'}</div>
                        <div style={{ marginTop: 4, fontSize: 13, color: '#a855f7' }}>{record.studentId || '—'}</div>
                        {record.regStatus ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', marginTop: 8, borderRadius: 999, background: regStatusStyle.bg, color: regStatusStyle.color, padding: '4px 10px', fontSize: 12, fontWeight: 700, textTransform: 'capitalize', lineHeight: 1 }}>
                            {record.regStatus}
                          </span>
                        ) : null}
                        <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.5, color: '#475569' }}>
                          {record.batchName || '—'}
                          {record.course ? `, ${record.course}` : ''}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                          <button
                            type="button"
                            onClick={() => handleCopyContact(record.contactNo)}
                            disabled={!record.contactNo}
                            title={record.contactNo ? 'Copy contact number' : 'No contact number'}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              borderRadius: 999,
                              background: '#e0ecff',
                              color: '#1d4ed8',
                              border: 'none',
                              padding: '6px 12px',
                              fontSize: 13,
                              fontWeight: 700,
                              cursor: record.contactNo ? 'pointer' : 'default',
                              opacity: record.contactNo ? 1 : 0.65,
                            }}
                          >
                            {copiedContact === record.contactNo ? 'Copied' : (record.contactNo || 'No Contact')}
                          </button>
                          <span style={{ display: 'inline-flex', alignItems: 'center', borderRadius: 999, background: '#e8fbf2', color: '#047857', padding: '6px 12px', fontSize: 13, fontWeight: 700 }}>
                            {`Dues ${record.noOfDues ?? '—'}`}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 14px', color: '#0f172a', fontWeight: 700 }}>Rs {fmt(record.dueAmount)}</td>
                      <td style={{ padding: '12px 14px', color: '#475569', whiteSpace: 'nowrap' }}>{formatDueDate(record.dueDate)}</td>
                      <td
                        style={{ padding: '12px 14px' }}
                        onDoubleClick={() => startEditingCell(record, 'dueStatus')}
                        title="Double-click to edit"
                      >
                        {isEditingCell(record.dueId, 'dueStatus') ? (
                          <select
                            autoFocus
                            value={editingValue}
                            onChange={(event) => {
                              const value = event.target.value;
                              setEditingValue(value);
                              saveInlineEdit(record, 'dueStatus', value);
                            }}
                            onBlur={() => setTimeout(() => stopEditingCell(), 0)}
                            disabled={isSavingCell(record.dueId, 'dueStatus')}
                            style={{ height: 36, minWidth: 140, borderRadius: 8, border: '1px solid #cbd5e1', padding: '0 10px', fontSize: 13, outline: 'none', background: '#fff' }}
                          >
                            {STATUS_OPTIONS.filter((status) => status !== 'all').map((status) => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        ) : (
                          <span style={{ display: 'inline-block', borderRadius: 999, background: statusStyle.bg, color: statusStyle.color, padding: '5px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                            {record.dueStatus || '—'}
                          </span>
                        )}
                      </td>
                      <td
                        style={{ padding: '12px 14px', color: '#0f172a', fontWeight: 600 }}
                        onDoubleClick={() => startEditingCell(record, 'confirmedAmount')}
                        title="Double-click to edit"
                      >
                        {isEditingCell(record.dueId, 'confirmedAmount') ? (
                          <input
                            autoFocus
                            type="number"
                            step="0.01"
                            value={editingValue}
                            onChange={(event) => setEditingValue(event.target.value)}
                            onBlur={() => saveInlineEdit(record, 'confirmedAmount', editingValue)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') saveInlineEdit(record, 'confirmedAmount', editingValue);
                              if (event.key === 'Escape') stopEditingCell();
                            }}
                            disabled={isSavingCell(record.dueId, 'confirmedAmount')}
                            style={{ width: 120, height: 36, borderRadius: 8, border: '1px solid #cbd5e1', padding: '0 10px', fontSize: 13, outline: 'none' }}
                          />
                        ) : (
                          <span style={{ cursor: 'pointer' }}>Rs {fmt(record.confirmedAmount)}</span>
                        )}
                      </td>
                      <td
                        style={{ padding: '12px 14px', color: '#475569', whiteSpace: 'nowrap' }}
                        onDoubleClick={() => startEditingCell(record, 'confirmDateToPay')}
                        title="Double-click to edit"
                      >
                        {isEditingCell(record.dueId, 'confirmDateToPay') ? (
                          <input
                            autoFocus
                            type="date"
                            value={editingValue}
                            onChange={(event) => setEditingValue(event.target.value)}
                            onBlur={() => saveInlineEdit(record, 'confirmDateToPay', editingValue)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') saveInlineEdit(record, 'confirmDateToPay', editingValue);
                              if (event.key === 'Escape') stopEditingCell();
                            }}
                            disabled={isSavingCell(record.dueId, 'confirmDateToPay')}
                            style={{ width: 150, height: 36, borderRadius: 8, border: '1px solid #cbd5e1', padding: '0 10px', fontSize: 13, outline: 'none' }}
                          />
                        ) : (
                          <span style={{ cursor: 'pointer' }}>{formatDueDate(record.confirmDateToPay)}</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <button
                          type="button"
                          onClick={() => openFollowUpModal(record)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, borderRadius: 10, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                        >
                          <span>Follow-up</span>
                          <span style={{ minWidth: 20, height: 20, borderRadius: 999, background: '#dbeafe', color: '#1e3a8a', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px', fontSize: 11, fontWeight: 700 }}>
                            {record.followupCount ?? 0}
                          </span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {inlineError && (
          <div style={{ borderRadius: 10, border: '1px solid #fecaca', background: '#fff1f2', color: '#be123c', fontSize: 13, padding: '10px 12px' }}>
            {inlineError}
          </div>
        )}
      </div>

      {courseSummaryModalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1150, display: 'grid', placeItems: 'center', padding: 20, animation: 'coordinatorDueModalFadeIn 180ms ease-out' }}>
          <div aria-hidden="true" onClick={() => setCourseSummaryModalOpen(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(15, 23, 42, 0.56)' }} />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="coordinator-course-summary-title"
            style={{ position: 'relative', width: '100%', maxWidth: 1080, maxHeight: '85vh', overflow: 'hidden', borderRadius: 24, background: '#f8fafc', boxShadow: '0 28px 60px rgba(15, 23, 42, 0.24)', display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' }}>
              <div>
                <h3 id="coordinator-course-summary-title" style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#fff' }}>Course Wise Due Summary</h3>
                <div style={{ marginTop: 6, fontSize: 13, color: '#cbd5e1' }}>
                  {formatMonthTitle(monthFilter)} • {departmentSummary.length} courses • {summary.totalStudents} students
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCourseSummaryModalOpen(false)}
                style={{ width: 38, height: 38, borderRadius: 999, border: '1px solid rgba(226, 232, 240, 0.24)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 20, cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: 24, overflowY: 'auto', display: 'grid', gap: 18 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <div style={{ background: '#fff', borderRadius: 16, padding: '16px 18px', border: '1px solid #fee2e2' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>Total Due</div>
                  <div style={{ marginTop: 8, fontSize: 22, fontWeight: 700, color: '#dc2626' }}>Rs {fmt(summary.totalDueAmount)}</div>
                </div>
                <div style={{ background: '#fff', borderRadius: 16, padding: '16px 18px', border: '1px solid #dcfce7' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>Collected</div>
                  <div style={{ marginTop: 8, fontSize: 22, fontWeight: 700, color: '#16a34a' }}>Rs {fmt(summary.totalPaidAmount)}</div>
                </div>
                <div style={{ background: '#fff', borderRadius: 16, padding: '16px 18px', border: '1px solid #dbeafe' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>Remaining</div>
                  <div style={{ marginTop: 8, fontSize: 22, fontWeight: 700, color: '#991b1b' }}>Rs {fmt(summary.remainingAmount)}</div>
                </div>
                <div style={{ background: '#fff', borderRadius: 16, padding: '16px 18px', border: '1px solid #ede9fe' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>Collection Rate</div>
                  <div style={{ marginTop: 8, fontSize: 22, fontWeight: 700, color: '#5b21b6' }}>{summary.collectionPct.toFixed(1)}%</div>
                </div>
              </div>

              {departmentSummary.length === 0 ? (
                <div style={{ background: '#fff', borderRadius: 16, border: '1px dashed #cbd5e1', padding: '28px 20px', color: '#64748b', textAlign: 'center' }}>
                  No dues found for your coordinated courses in the selected filters.
                </div>
              ) : (
                <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 18, border: '1px solid #e2e8f0' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        {['Course', 'Students', 'Total Due', 'Collected', 'Committed', 'Remaining', 'Follow-ups', 'Progress'].map((header) => (
                          <th key={header} style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {departmentSummary.map((item) => (
                        <tr key={item.course} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '16px', minWidth: 220 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', lineHeight: 1.5 }}>{item.course}</div>
                          </td>
                          <td style={{ padding: '16px', color: '#0f172a', fontWeight: 700 }}>{item.totalStudents}</td>
                          <td style={{ padding: '16px', color: '#dc2626', fontWeight: 700 }}>Rs {fmt(item.totalDueAmount)}</td>
                          <td style={{ padding: '16px', color: '#16a34a', fontWeight: 700 }}>Rs {fmt(item.totalPaidAmount)}</td>
                          <td style={{ padding: '16px', color: '#0369a1', fontWeight: 700 }}>Rs {fmt(item.totalConfirmedAmount)}</td>
                          <td style={{ padding: '16px', color: '#991b1b', fontWeight: 700 }}>Rs {fmt(item.remainingAmount)}</td>
                          <td style={{ padding: '16px', color: '#0f172a', fontWeight: 700 }}>{item.followupCount}</td>
                          <td style={{ padding: '16px', minWidth: 170 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: '#64748b' }}>
                              <span>Progress</span>
                              <span style={{ fontWeight: 700, color: '#0f172a' }}>{item.collectionPct.toFixed(1)}%</span>
                            </div>
                            <div style={{ height: 8, borderRadius: 999, background: '#e2e8f0', overflow: 'hidden' }}>
                              <div style={{ width: `${item.collectionPct}%`, height: '100%', borderRadius: 999, background: item.collectionPct >= 70 ? '#16a34a' : item.collectionPct >= 35 ? '#d97706' : '#dc2626' }} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {staffContributionModalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1150, display: 'grid', placeItems: 'center', padding: 20, animation: 'coordinatorDueModalFadeIn 180ms ease-out' }}>
          <div aria-hidden="true" onClick={() => setStaffContributionModalOpen(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(15, 23, 42, 0.56)' }} />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="staff-contribution-title"
            style={{ position: 'relative', width: '100%', maxWidth: 640, maxHeight: '80vh', overflow: 'hidden', borderRadius: 24, background: '#f8fafc', boxShadow: '0 28px 60px rgba(15, 23, 42, 0.24)', display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, background: 'linear-gradient(135deg, #064e3b 0%, #065f46 100%)' }}>
              <div>
                <h3 id="staff-contribution-title" style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#fff' }}>Staff Contribution</h3>
                <div style={{ marginTop: 6, fontSize: 13, color: '#a7f3d0' }}>
                  Follow-up counts per staff • {monthFilter ? formatMonthTitle(monthFilter) : 'All Months'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setStaffContributionModalOpen(false)}
                style={{ width: 38, height: 38, borderRadius: 999, border: '1px solid rgba(226, 232, 240, 0.24)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 20, cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: 24, overflowY: 'auto' }}>
              {staffContributionLoading ? (
                <div style={{ display: 'grid', gap: 12 }}>
                  {[1, 2, 3].map((i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, borderRadius: 16, background: '#fff', border: '1px solid #e2e8f0', padding: '14px 16px' }}>
                      <div style={{ width: 48, height: 48, borderRadius: 999, background: '#e2e8f0' }} className="animate-pulse" />
                      <div style={{ flex: 1 }}>
                        <div style={{ height: 14, width: 140, borderRadius: 999, background: '#e2e8f0' }} className="animate-pulse" />
                        <div style={{ marginTop: 8, height: 11, width: 80, borderRadius: 999, background: '#f1f5f9' }} className="animate-pulse" />
                      </div>
                      <div style={{ height: 28, width: 50, borderRadius: 10, background: '#e2e8f0' }} className="animate-pulse" />
                    </div>
                  ))}
                </div>
              ) : staffContribution.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '28px 20px', color: '#64748b', fontSize: 14 }}>
                  No follow-up activity recorded for this period.
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 12 }}>
                  {staffContribution.map((staff, index) => {
                    const medal = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : null;
                    return (
                      <div
                        key={staff.staffId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 14,
                          borderRadius: 16,
                          background: '#fff',
                          border: `1px solid ${index === 0 ? '#fde68a' : '#e2e8f0'}`,
                          padding: '14px 16px',
                          boxShadow: index === 0 ? '0 2px 12px rgba(251, 191, 36, 0.15)' : 'none',
                        }}
                      >
                        <div style={{ position: 'relative', flexShrink: 0 }}>
                          {staff.staffImagePath ? (
                            <img
                              src={`${process.env.REACT_APP_API_BASE_URL || ''}${staff.staffImagePath}`}
                              alt={staff.staffName || 'Staff'}
                              style={{ width: 48, height: 48, borderRadius: 999, objectFit: 'cover', border: '2px solid #e2e8f0' }}
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            />
                          ) : null}
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: 999,
                              background: 'linear-gradient(135deg, #0f172a 0%, #1e40af 100%)',
                              display: staff.staffImagePath ? 'none' : 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#fff',
                              fontSize: 18,
                              fontWeight: 700,
                              border: '2px solid #e2e8f0',
                            }}
                          >
                            {(staff.staffName || '?').charAt(0).toUpperCase()}
                          </div>
                          {medal && (
                            <span style={{ position: 'absolute', bottom: -4, right: -4, fontSize: 16 }}>{medal}</span>
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {staff.staffName || `Staff #${staff.staffId}`}
                          </div>
                          <div style={{ marginTop: 3, fontSize: 12, color: '#64748b' }}>Follow-ups this period</div>
                        </div>
                        <div style={{ flexShrink: 0, minWidth: 52, textAlign: 'center' }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color: index === 0 ? '#d97706' : '#0f172a' }}>
                            {staff.followupCount}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {followUpModal.isOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', justifyContent: 'flex-start' }}>
          <div aria-hidden="true" onClick={closeFollowUpModal} style={{ position: 'absolute', inset: 0, background: 'rgba(15, 23, 42, 0.52)' }} />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="coordinator-course-due-followup-title"
            style={{ position: 'relative', width: '100%', maxWidth: 440, height: '100%', background: '#fff', boxShadow: '18px 0 48px rgba(15, 23, 42, 0.18)', animation: 'coordinatorDueFollowupSlideIn 220ms ease-out', display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <h3 id="coordinator-course-due-followup-title" style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Coordinator Follow-up</h3>
                <div style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>
                  {followUpModal.row?.studentName || 'Student'}
                  {followUpModal.row?.studentId ? ` • ${followUpModal.row.studentId}` : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={closeFollowUpModal}
                style={{ width: 34, height: 34, borderRadius: 999, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 18, cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: 20, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0', padding: 14, display: 'grid', gap: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.4 }}>Course</div>
                    <div style={{ marginTop: 4, fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{followUpModal.row?.course || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.4 }}>Batch</div>
                    <div style={{ marginTop: 4, fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{followUpModal.row?.batchName || '—'}</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.4 }}>Total Due</div>
                    <div style={{ marginTop: 4, fontSize: 15, fontWeight: 700, color: '#dc2626' }}>Rs {fmt(followUpModal.row?.dueAmount)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.4 }}>Confirmed</div>
                    <div style={{ marginTop: 4, fontSize: 15, fontWeight: 700, color: '#0369a1' }}>Rs {fmt(followUpModal.row?.confirmedAmount)}</div>
                  </div>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4 }}>Comment</label>
                <textarea
                  value={followUpForm.comment}
                  onChange={(event) => setFollowUpForm((previous) => ({ ...previous, comment: event.target.value }))}
                  rows={4}
                  placeholder="Add your coordinator follow-up note"
                  style={{ width: '100%', borderRadius: 10, border: '1px solid #cbd5e1', padding: '10px 12px', fontSize: 14, resize: 'vertical', outline: 'none' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4 }}>Next Reminder Date</label>
                <input
                  type="date"
                  value={followUpForm.nextReminderDate}
                  onChange={(event) => setFollowUpForm((previous) => ({ ...previous, nextReminderDate: event.target.value }))}
                  style={{ width: '100%', height: 42, borderRadius: 10, border: '1px solid #cbd5e1', padding: '0 12px', fontSize: 14, outline: 'none' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4 }}>Next Reminder Comment</label>
                <textarea
                  value={followUpForm.nextReminderComment}
                  onChange={(event) => setFollowUpForm((previous) => ({ ...previous, nextReminderComment: event.target.value }))}
                  rows={2}
                  placeholder="Optional note for the next reminder"
                  style={{ width: '100%', borderRadius: 10, border: '1px solid #cbd5e1', padding: '10px 12px', fontSize: 14, resize: 'vertical', outline: 'none' }}
                />
              </div>

              {followUpForm.error && (
                <div style={{ borderRadius: 10, border: '1px solid #fecaca', background: '#fff1f2', color: '#be123c', fontSize: 13, padding: '10px 12px' }}>
                  {followUpForm.error}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={closeFollowUpModal}
                  style={{ height: 40, padding: '0 16px', borderRadius: 10, border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={submitFollowUp}
                  disabled={followUpForm.submitting}
                  style={{ flex: 1, height: 40, padding: '0 16px', borderRadius: 10, border: 'none', background: followUpForm.submitting ? '#93c5fd' : '#2563eb', color: '#fff', fontSize: 14, fontWeight: 700, cursor: followUpForm.submitting ? 'not-allowed' : 'pointer' }}
                >
                  {followUpForm.submitting ? 'Saving...' : 'Save Follow-up'}
                </button>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 10 }}>History</div>
                {followUpForm.loadingHistory ? (
                  <div style={{ borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', padding: '14px 12px', fontSize: 13, color: '#64748b' }}>Loading history...</div>
                ) : followUpHistory.length === 0 ? (
                  <div style={{ borderRadius: 10, border: '1px dashed #cbd5e1', background: '#fff', padding: '14px 12px', fontSize: 13, color: '#64748b' }}>No follow-up history yet.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 10 }}>
                    {followUpHistory.map((item) => (
                      <div key={item.id} style={{ borderRadius: 12, border: '1px solid #e2e8f0', background: '#fff', padding: 14 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{item.createdBy || 'Coordinator'}</div>
                          <div style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{formatDateTime(item.commentDate || item.createdDate)}</div>
                        </div>
                        <div style={{ marginTop: 8, fontSize: 14, color: '#334155', lineHeight: 1.5 }}>{item.comment || '—'}</div>
                        {(item.nextReminderDate || item.nextReminderComment) && (
                          <div style={{ marginTop: 10, borderRadius: 10, background: '#f8fafc', padding: '10px 12px' }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Next Reminder</div>
                            <div style={{ marginTop: 4, fontSize: 13, color: '#0f172a' }}>{formatDateTime(item.nextReminderDate)}</div>
                            {item.nextReminderComment ? <div style={{ marginTop: 4, fontSize: 13, color: '#475569' }}>{item.nextReminderComment}</div> : null}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}